import React, { Dispatch, SetStateAction, useState } from 'react';
import { axiosKK } from '../services/networkRequest';
import SubmitButton from './SubmitButton';
import { RouteComponentProps } from 'react-router';
import errorMessage from '../services/errorMessage';
import { PhoneNumberUtil } from 'google-libphonenumber';
import useGtmEventTracking from './useGtmEventTracking';
import { BoxData, Dimensions, Product } from '../models/APIModels';
import { Address } from './AddressLookup';
import { User, UserState } from '../contexts/UserContext';
import { Role } from '../utilities/Role';

export interface RegistrationProps extends RouteComponentProps {
  postRegistration: () => any;
  close?: () => void;
  path: string;
  createAdminAccount: boolean;
  setUserState: Dispatch<SetStateAction<UserState>>;
  userState: UserState;
  showToggle?: boolean;
}

interface ConfirmationState {
  boxes: BoxData[];
  collectionAddress: Address;
  collectionDate: Date;
  deliveryAddress: Address;
  dontKnowAddress: boolean;
  deliveryDate: Date;
  dontKnowDate: boolean;
  storageRequired: boolean;
  suitcases?: Dimensions[];
  suitcaseData?: Product;
  isFirstTimeTriggered?: boolean;
  fromConfirmation?: boolean;
}

interface FieldErrors {
  email?: string;
  password?: string;
  confirm?: string;
  firstname?: string;
  lastname?: string;
  phoneNumber?: string;
  role?: string;
}

const emailRegExp =
  /^([^\x00-\x7F]|[a-zA-Z0-9_\-.])+@([^\x00-\x7F]|[a-zA-Z0-9_\-.])+\.([^\x00-\x7F]|[a-zA-Z0-9_\-.]){2,}$/;

const phoneUtil = PhoneNumberUtil.getInstance();

export default function RegistrationSection({
  history,
  postRegistration,
  close,
  path,
  createAdminAccount,
  setUserState,
  showToggle = true,
}: RegistrationProps) {
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [role, setRole] = useState('');
  const [networkError, setNetworkError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const sendDataToGTM = useGtmEventTracking();
  const confirmationState = history.location.state as ConfirmationState;

  const validate = (): boolean => {
    const errors: FieldErrors = {};

    if (email.length < 5) {
      errors.email = 'Email Address is too short';
    } else if (!email.match(emailRegExp)?.includes(email)) {
      errors.email = 'Email Address is Invalid';
    }

    if (!firstname || firstname.length < 2) {
      errors.firstname = 'First Name must be at least 2 characters';
    }

    if (!lastname || lastname.length < 2) {
      errors.lastname = 'Last Name must be at least 2 characters';
    }

    if (!password || password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!confirm || confirm !== password) {
      errors.confirm = 'Confirm Password did not match';
    }

    if (phoneNumber.length < 9) {
      errors.phoneNumber = 'Phone Number is too short';
    } else {
      try {
        const UKPhoneNumber = phoneUtil.parse(phoneNumber, 'GB');
        if (!phoneUtil.isValidNumber(UKPhoneNumber)) {
          errors.phoneNumber = 'Phone Number is Invalid';
        }
      } catch {
        errors.phoneNumber = 'Phone Number is Invalid';
      }
    }

    if (createAdminAccount && !role) {
      errors.role = 'Role is not registered';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setNetworkError(undefined);

    const values: any = { email, password, confirm, firstname, lastname, phoneNumber };
    if (createAdminAccount) values.role = role;

    axiosKK
      .post(path, values)
      .then((response) => {
        sendDataToGTM({ event: 'account_register' });
        if (confirmationState && confirmationState.fromConfirmation) {
          const userResponse = response.data.customer || response.data.user;
          localStorage.setItem(
            'user',
            JSON.stringify({
              ...userResponse,
              token: response.data.jwt,
            }),
          );
          const user = userResponse as User;
          setUserState((u) => ({
            ...u,
            updateUser: (user: User) => {
              setUserState((u2) => ({
                ...u2,
                user,
              }));
            },
            user: user,
          }));

          axiosKK.defaults.headers.common['Authorization'] =
            'bearer ' + response.data.jwt;

          history.goBack();
        } else {
          history.push('/storage/boxes');
          postRegistration();
        }
      })
      .catch((error) => {
        errorMessage(setNetworkError)(error);
        setIsSubmitting(false);
      });
  };

  return (
    <div className="auth-card">
      {close && (
        <div className="auth-card__close">
          <button type="button" onClick={close}>&times;</button>
        </div>
      )}

      <button type="button" className="auth-card__back" onClick={() => history.goBack()}>
        &larr; Back
      </button>

      <div className="auth-card__header">
        <h3>{createAdminAccount ? 'Create Account' : 'Create Your Account'}</h3>
        <p>
          {createAdminAccount
            ? 'Set up a new staff account'
            : 'Quick registration to manage your bookings'}
        </p>
      </div>

      {networkError && (
        <div className="auth-card__error">{networkError}</div>
      )}

      <form className="auth-card__form" onSubmit={handleSubmit}>
        <div className="auth-card__row">
          <div className="auth-card__field">
            <label htmlFor="reg-firstname">First Name</label>
            <input
              id="reg-firstname"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              placeholder="Enter your first name"
            />
            {fieldErrors.firstname && (
              <span className="field-error">{fieldErrors.firstname}</span>
            )}
          </div>
          <div className="auth-card__field">
            <label htmlFor="reg-lastname">Last Name</label>
            <input
              id="reg-lastname"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              placeholder="Enter your last name"
            />
            {fieldErrors.lastname && (
              <span className="field-error">{fieldErrors.lastname}</span>
            )}
          </div>
        </div>

        <div className="auth-card__field">
          <label htmlFor="reg-email">Email</label>
          <input
            id="reg-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
          />
          {fieldErrors.email && (
            <span className="field-error">{fieldErrors.email}</span>
          )}
        </div>

        <div className="auth-card__row">
          <div className="auth-card__field">
            <label htmlFor="reg-password">Password</label>
            <input
              id="reg-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 characters"
            />
            {fieldErrors.password && (
              <span className="field-error">{fieldErrors.password}</span>
            )}
          </div>
          <div className="auth-card__field">
            <label htmlFor="reg-confirm">Confirm Password</label>
            <input
              id="reg-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm your password"
            />
            {fieldErrors.confirm && (
              <span className="field-error">{fieldErrors.confirm}</span>
            )}
          </div>
        </div>

        <div className="auth-card__field">
          <label htmlFor="reg-phone">Phone (UK Number)</label>
          <input
            id="reg-phone"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="07XXX XXXXXX"
          />
          {fieldErrors.phoneNumber && (
            <span className="field-error">{fieldErrors.phoneNumber}</span>
          )}
        </div>

        {createAdminAccount && (
          <div className="auth-card__field auth-card__field--radio">
            <label>Role (For Administrative Use)</label>
            <section>
              <div className="radio-container">
                <input
                  type="radio"
                  id="role_admin"
                  name="role"
                  value={Role.Admin}
                  checked={role === Role.Admin}
                  onChange={(e) => setRole(e.target.value)}
                />
                <label htmlFor="role_admin">Admin</label>
              </div>
              <div className="radio-container">
                <input
                  type="radio"
                  id="role_warehouse"
                  name="role"
                  value={Role.Warehouse}
                  checked={role === Role.Warehouse}
                  onChange={(e) => setRole(e.target.value)}
                />
                <label htmlFor="role_warehouse">Warehouse</label>
              </div>
            </section>
            {fieldErrors.role && (
              <span className="field-error">{fieldErrors.role}</span>
            )}
          </div>
        )}

        <SubmitButton disabled={isSubmitting} isSubmitting={isSubmitting}>
          {createAdminAccount ? 'Create Account' : 'Register'}
        </SubmitButton>
      </form>

      {showToggle && !createAdminAccount && (
        <div className="auth-card__toggle">
          <p>
            Already have an account?{' '}
            <button type="button" onClick={() => history.push('/login')}>
              Log in
            </button>
          </p>
        </div>
      )}
    </div>
  );
}
